import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Activity, 
  Building2, Target, AlertCircle, CheckCircle2, BarChart3,
  Eye, Glasses, Calendar, PieChart, X, Download, Filter
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ScatterChart, Scatter, ComposedChart, Area
} from 'recharts';

interface Location {
  id: string;
  name: string;
  code: string;
}

interface MetricData {
  name: string;
  value: number;
  target: number;
  category: string;
  trend?: number;
  status: 'green' | 'yellow' | 'red';
}

interface FinancialData {
  location: string;
  revenue: number;
  cogs: number;
  labor: number;
  grossProfit: number;
  opex: number;
  ebitda: number;
  margin: number;
}

const PEDashboard: React.FC = () => {
  const [selectedLocations, setSelectedLocations] = useState<string[]>(['all']);
  const [timeRange, setTimeRange] = useState('ytd');
  const [viewMode, setViewMode] = useState('executive');
  const [benchmarkMode, setBenchmarkMode] = useState('50th');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  // Mock data - in production this would come from your API
  const locations: Location[] = [
    { id: 'all', name: 'All Locations', code: 'ALL' },
    { id: 'reg', name: 'REG', code: 'REG' },
    { id: 'dec', name: 'Decatur', code: 'DEC' },
    { id: 'jcity', name: 'Johnson City', code: 'JCITY' },
    { id: 'ecobb', name: 'East Cobb', code: 'ECOBB' },
    { id: 'rvd', name: 'Riverdale', code: 'RVD' },
    { id: 'ctn', name: 'Canton', code: 'CTN' },
    { id: 'lvl', name: 'Lawrenceville', code: 'LVL' }
  ];

  // Financial data from P&L
  const financialData: FinancialData[] = [
    { location: 'JCITY', revenue: 322407, cogs: 84757, labor: 157672, grossProfit: 79978, opex: 64945, ebitda: 15033, margin: 4.7 },
    { location: 'RVD', revenue: 142303, cogs: 14858, labor: 55873, grossProfit: 71572, opex: 82500, ebitda: -10928, margin: -7.7 },
    { location: 'ECOBB', revenue: 188659, cogs: 62060, labor: 98618, grossProfit: 27981, opex: 58380, ebitda: -30400, margin: -16.1 },
    { location: 'CTN', revenue: 21305, cogs: 8244, labor: 30835, grossProfit: -17775, opex: 66001, ebitda: -83775, margin: -393.3 },
    { location: 'LVL', revenue: 252475, cogs: 39273, labor: 152191, grossProfit: 61011, opex: 75101, ebitda: -14090, margin: -5.6 }
  ];

  // Enhanced aggregation logic
  const aggregatedMetrics = useMemo(() => {
    const filtered = selectedLocations.includes('all') 
      ? financialData 
      : financialData.filter(d => selectedLocations.includes(d.location.toLowerCase()));
    
    const totals = filtered.reduce((acc, curr) => ({
      revenue: acc.revenue + curr.revenue,
      cogs: acc.cogs + curr.cogs,
      labor: acc.labor + curr.labor,
      grossProfit: acc.grossProfit + curr.grossProfit,
      opex: acc.opex + curr.opex,
      ebitda: acc.ebitda + curr.ebitda,
      count: acc.count + 1
    }), { revenue: 0, cogs: 0, labor: 0, grossProfit: 0, opex: 0, ebitda: 0, count: 0 });

    // For metrics that should be averaged across locations
    const avgMetrics = {
      avgRevenuePerExam: 452, // This would be calculated from actual exam counts
      avgTicketSize: 580,
      contactLensCaptureRate: 58,
      completePairCapture: 25
    };

    return {
      totalRevenue: totals.revenue,
      avgRevenuePerLocation: totals.revenue / totals.count,
      ebitdaMargin: (totals.ebitda / totals.revenue) * 100,
      laborEfficiency: (totals.labor / totals.revenue) * 100,
      grossMargin: (totals.grossProfit / totals.revenue) * 100,
      locationsCount: totals.count,
      profitableLocations: filtered.filter(d => d.ebitda > 0).length,
      // Averaged metrics
      avgRevenuePerExam: avgMetrics.avgRevenuePerExam,
      avgTicketSize: avgMetrics.avgTicketSize,
      contactLensCaptureRate: avgMetrics.contactLensCaptureRate,
      completePairCapture: avgMetrics.completePairCapture
    };
  }, [selectedLocations]);

  // Multi-select location handler
  const handleLocationToggle = (locationId: string) => {
    if (locationId === 'all') {
      setSelectedLocations(['all']);
    } else {
      setSelectedLocations(prev => {
        const newSelection = prev.includes(locationId)
          ? prev.filter(id => id !== locationId)
          : [...prev.filter(id => id !== 'all'), locationId];
        
        return newSelection.length === 0 ? ['all'] : newSelection;
      });
    }
  };

  // Enhanced KPI Card with gradient backgrounds
  const KPICard = ({ title, value, target, change, icon: Icon, status }: any) => (
    <Card className={`relative overflow-hidden transition-all hover:shadow-xl transform hover:-translate-y-1 ${
      status === 'green' ? 'border-green-500 bg-gradient-to-br from-green-50 to-green-100' : 
      status === 'yellow' ? 'border-yellow-500 bg-gradient-to-br from-yellow-50 to-yellow-100' : 
      'border-red-500 bg-gradient-to-br from-red-50 to-red-100'
    } border-2`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {target && (
              <p className="text-sm text-gray-600">Target: {target}</p>
            )}
            {change !== undefined && (
              <div className={`flex items-center text-sm font-semibold ${change >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {change >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                {Math.abs(change)}% {change >= 0 ? 'increase' : 'decrease'}
              </div>
            )}
          </div>
          <div className={`p-4 rounded-full ${
            status === 'green' ? 'bg-green-200' : 
            status === 'yellow' ? 'bg-yellow-200' : 
            'bg-red-200'
          }`}>
            <Icon className={`w-10 h-10 ${
              status === 'green' ? 'text-green-700' : 
              status === 'yellow' ? 'text-yellow-700' : 
              'text-red-700'
            }`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Enhanced Performance Gauge with better visuals
  const PerformanceGauge = ({ label, value, target, industry }: any) => {
    const percentage = (value / target) * 100;
    const industryPercentage = (industry / target) * 100;
    
    return (
      <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-800">{label}</span>
          <span className="text-sm font-medium text-gray-600">{value} / {target}</span>
        </div>
        <div className="relative">
          <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-1000 ${
                percentage >= 100 ? 'bg-green-500' : 
                percentage >= 80 ? 'bg-yellow-500' : 
                'bg-red-500'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <div 
            className="absolute top-0 h-4 w-1 bg-blue-700 shadow-lg" 
            style={{ left: `${Math.min(industryPercentage, 100)}%` }}
            title={`Industry: ${industry}`}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>0%</span>
          <span className="text-blue-700 font-medium">Industry: {industry}</span>
          <span>Target: {target}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      {/* Header with modern gradient */}
      <div className="mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-5xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
              BoltOS Executive Dashboard
            </h1>
            <p className="text-gray-300 text-lg">Private Equity Performance Overview • Real-time Analytics</p>
          </div>
          <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Enhanced Filter Bar */}
      <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-2xl p-6 mb-8 border border-white/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-200 mb-2 block">Time Period</label>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mtd">Month to Date</SelectItem>
                <SelectItem value="qtd">Quarter to Date</SelectItem>
                <SelectItem value="ytd">Year to Date</SelectItem>
                <SelectItem value="ly">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-200 mb-2 block">View Mode</label>
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="executive">Executive Summary</SelectItem>
                <SelectItem value="financial">Financial Deep Dive</SelectItem>
                <SelectItem value="operational">Operational Metrics</SelectItem>
                <SelectItem value="benchmark">Industry Comparison</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-200 mb-2 block">Benchmark</label>
            <Select value={benchmarkMode} onValueChange={setBenchmarkMode}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="50th">50th Percentile</SelectItem>
                <SelectItem value="75th">75th Percentile</SelectItem>
                <SelectItem value="90th">90th Percentile</SelectItem>
                <SelectItem value="top10">Top 10%</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Selected locations tags */}
        {!selectedLocations.includes('all') && (
          <div className="mt-4 flex flex-wrap gap-2">
            {selectedLocations.map(locId => {
              const location = locations.find(l => l.id === locId);
              return location ? (
                <Badge key={locId} variant="secondary" className="bg-white/20 text-white">
                  {location.name}
                  <X 
                    className="w-3 h-3 ml-1 cursor-pointer" 
                    onClick={() => handleLocationToggle(locId)}
                  />
                </Badge>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* KPI Cards with enhanced styling */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <KPICard
          title="Total Revenue"
          value={`$${(aggregatedMetrics.totalRevenue / 1000).toFixed(0)}K`}
          target="$2M"
          change={15}
          icon={DollarSign}
          status={aggregatedMetrics.totalRevenue > 900000 ? 'green' : 'yellow'}
        />
        <KPICard
          title="EBITDA Margin"
          value={`${aggregatedMetrics.ebitdaMargin.toFixed(1)}%`}
          target="15%"
          change={-2.3}
          icon={TrendingUp}
          status={aggregatedMetrics.ebitdaMargin > 10 ? 'green' : aggregatedMetrics.ebitdaMargin > 0 ? 'yellow' : 'red'}
        />
        <KPICard
          title="Avg Rev/Exam"
          value={`$${aggregatedMetrics.avgRevenuePerExam}`}
          target="$500"
          change={8}
          icon={Eye}
          status={aggregatedMetrics.avgRevenuePerExam > 450 ? 'green' : 'yellow'}
        />
        <KPICard
          title="Labor Efficiency"
          value={`${aggregatedMetrics.laborEfficiency.toFixed(1)}%`}
          target="29%"
          change={-5}
          icon={Users}
          status={aggregatedMetrics.laborEfficiency < 35 ? 'green' : aggregatedMetrics.laborEfficiency < 45 ? 'yellow' : 'red'}
        />
        <KPICard
          title="Locations Performance"
          value={`${aggregatedMetrics.profitableLocations}/${aggregatedMetrics.locationsCount}`}
          target="100%"
          change={0}
          icon={Building2}
          status={aggregatedMetrics.profitableLocations > 4 ? 'green' : aggregatedMetrics.profitableLocations > 2 ? 'yellow' : 'red'}
        />
      </div>

      {/* Main Content with Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="space-y-6">
        <TabsContent value="executive" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Financial Performance Chart */}
            <Card className="lg:col-span-2 bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Financial Performance by Location</span>
                  <Badge className="bg-green-500/20 text-green-300 border-green-300">Live</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={financialData}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.2}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="location" stroke="rgba(255,255,255,0.6)" />
                    <YAxis stroke="rgba(255,255,255,0.6)" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '8px' }}
                      formatter={(value: any) => `$${(value / 1000).toFixed(0)}K`} 
                    />
                    <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.8)' }} />
                    <Bar dataKey="revenue" fill="url(#revenueGradient)" name="Revenue" />
                    <Bar dataKey="grossProfit" fill="#10B981" name="Gross Profit" opacity={0.8} />
                    <Line type="monotone" dataKey="margin" stroke="#F59E0B" strokeWidth={3} name="EBITDA %" yAxisId="right" />
                    <YAxis yAxisId="right" orientation="right" stroke="rgba(255,255,255,0.6)" />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Enhanced Location Scorecard */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Location Scorecard</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {financialData.map(location => {
                  const isSelected = selectedLocations.includes('all') || selectedLocations.includes(location.location.toLowerCase());
                  return (
                    <div 
                      key={location.location} 
                      className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                        isSelected ? 'bg-white/20' : 'bg-white/10 opacity-50'
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-white">{location.location}</p>
                        <p className="text-sm text-gray-300">
                          Revenue: ${(location.revenue / 1000).toFixed(0)}K
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-xl ${location.margin > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {location.margin.toFixed(1)}%
                        </p>
                        <p className="text-xs text-gray-400">EBITDA Margin</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Operational Excellence Dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Unit Economics vs Industry */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Unit Economics vs Industry Benchmarks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <PerformanceGauge 
                    label="Revenue per Exam" 
                    value={aggregatedMetrics.avgRevenuePerExam} 
                    target={500} 
                    industry={391}
                  />
                  <PerformanceGauge 
                    label="Contact Lens Capture Rate" 
                    value={aggregatedMetrics.contactLensCaptureRate} 
                    target={65} 
                    industry={60}
                  />
                  <PerformanceGauge 
                    label="Complete Pair Capture" 
                    value={aggregatedMetrics.completePairCapture} 
                    target={45} 
                    industry={44}
                  />
                  <PerformanceGauge 
                    label="Average Ticket Size" 
                    value={aggregatedMetrics.avgTicketSize} 
                    target={700} 
                    industry={600}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Cost Structure Breakdown */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Cost Structure Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RePieChart>
                    <Pie
                      data={[
                        { name: 'Labor (COL)', value: aggregatedMetrics.laborEfficiency, fill: '#EF4444' },
                        { name: 'COGS', value: 22.6, fill: '#F59E0B' },
                        { name: 'Rent', value: 15.8, fill: '#3B82F6' },
                        { name: 'Other OpEx', value: 100 - aggregatedMetrics.laborEfficiency - 22.6 - 15.8, fill: '#10B981' }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {[0, 1, 2, 3].map((_, index) => (
                        <Cell key={`cell-${index}`} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'rgba(0,0,0,0.8)', border: 'none', borderRadius: '8px' }}
                    />
                  </RePieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="financial" className="space-y-6">
          {/* Financial deep dive content */}
          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Detailed P&L Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add detailed financial tables and charts here */}
              <p className="text-gray-300">Financial deep dive content coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operational" className="space-y-6">
          {/* Operational metrics content */}
          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Operational Excellence Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add operational metrics here */}
              <p className="text-gray-300">Operational metrics content coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="benchmark" className="space-y-6">
          {/* Industry benchmark content */}
          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Industry Benchmark Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add benchmark analysis here */}
              <p className="text-gray-300">Benchmark analysis content coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Enhanced Action Items */}
      <Card className="mt-8 bg-gradient-to-r from-purple-900/50 to-blue-900/50 backdrop-blur-md border-white/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center text-2xl">
            <AlertCircle className="w-6 h-6 mr-3 text-yellow-400" />
            Priority Action Items for Value Creation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-red-500/20 rounded-xl border border-red-400/50">
              <h4 className="font-bold text-red-300 mb-3 text-lg">Immediate (0-30 days)</h4>
              <ul className="space-y-2 text-sm text-red-100">
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Reduce labor costs to 35% target (save $1.2M annually)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Close or restructure CTN location (eliminate -$83K loss)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Implement dynamic pricing model (+5% revenue)</span>
                </li>
              </ul>
            </div>
            <div className="p-6 bg-yellow-500/20 rounded-xl border border-yellow-400/50">
              <h4 className="font-bold text-yellow-300 mb-3 text-lg">Short-term (30-90 days)</h4>
              <ul className="space-y-2 text-sm text-yellow-100">
                <li className="flex items-start">
                  <Target className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Increase CL capture rate to 65% (+$400K revenue)</span>
                </li>
                <li className="flex items-start">
                  <Target className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Launch 2nd pair promotion (+15% optical revenue)</span>
                </li>
                <li className="flex items-start">
                  <Target className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Optimize provider schedules (+10% capacity)</span>
                </li>
              </ul>
            </div>
            <div className="p-6 bg-green-500/20 rounded-xl border border-green-400/50">
              <h4 className="font-bold text-green-300 mb-3 text-lg">Medium-term (90-180 days)</h4>
              <ul className="space-y-2 text-sm text-green-100">
                <li className="flex items-start">
                  <TrendingUp className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Achieve 15% EBITDA margin ($2.9M EBITDA)</span>
                </li>
                <li className="flex items-start">
                  <TrendingUp className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Scale profitable locations (3 new sites)</span>
                </li>
                <li className="flex items-start">
                  <TrendingUp className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Deploy AI revenue optimization (+8% revenue)</span>
                </li>
              </ul>
            </div>
          </div>
          
          {/* ROI Summary */}
          <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
            <p className="text-center text-white font-semibold">
              Total Value Creation Opportunity: 
              <span className="text-2xl ml-2 text-green-400">$4.2M</span>
              <span className="text-sm ml-2 text-gray-300">(+45% EBITDA improvement)</span>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PEDashboard; 